import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonIcon from '@mui/icons-material/Person';
import InventoryIcon from '@mui/icons-material/Inventory';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import WarehouseIcon from '@mui/icons-material/Warehouse';

import { API_BASE_URL } from '../../../services/api';
import type { ActivityType } from '../../../types/activityType';
import type { ActivityFormData } from './types';
import { formatContactFullName } from '../../../utils/contactName';

const SELECTED_CHIP_HEIGHT = 48;

interface ActivityFormItemProps {
  activity: ActivityFormData;
  index: number;
  activityTypes: ActivityType[];
  operationTypesLoaded: boolean;
  showRemoveButton: boolean;
  /** Per-activity server-side validation errors returned by the backend (HTTP 422). */
  serverErrors?: string[];
  /** Current balance of the destination stock for this product (fetched from availability API). */
  stockBalance?: number;
  /** Current balance of the source stock for this product (BETWEEN_STOCKS only). */
  stockFromBalance?: number;
  /** Weighted average cost of one unit currently held in the destination stock. */
  stockAverageUnitCost?: number;
  /** Weighted average cost of one unit currently held in the source stock (BETWEEN_STOCKS only). */
  stockFromAverageUnitCost?: number;
  onNavigateToAvailability?: (productId: number) => void;
  onUpdate: (updater: (prev: ActivityFormData) => ActivityFormData) => void;
  onCopy: () => void;
  onRemove: () => void;
  onRemoveProduct: () => void;
  onRemoveClient: () => void;
  onRemoveProvider: () => void;
  onRemoveStock: () => void;
  onRemoveStockFrom: () => void;
  onOpenProductSelector: () => void;
  onOpenClientSelector: () => void;
  onOpenProviderSelector: () => void;
  onOpenStockSelector: () => void;
  onOpenStockFromSelector: () => void;
}

export default function ActivityFormItem({
  activity,
  index,
  activityTypes,
  operationTypesLoaded,
  showRemoveButton,
  serverErrors,
  stockBalance,
  stockFromBalance,
  stockAverageUnitCost,
  stockFromAverageUnitCost,
  onNavigateToAvailability,
  onUpdate,
  onCopy,
  onRemove,
  onRemoveProduct,
  onRemoveClient,
  onRemoveProvider,
  onRemoveStock,
  onRemoveStockFrom,
  onOpenProductSelector,
  onOpenClientSelector,
  onOpenProviderSelector,
  onOpenStockSelector,
  onOpenStockFromSelector,
}: ActivityFormItemProps) {
  
  // Product and client are valid for any type, so they survive a type change
  // (e.g. a pre-order conversion prefills them). Price only applies to STOCK_IN/STOCK_OUT.
  const setActivityTypeAndClear = (prev: ActivityFormData, newActivityTypeId: number): ActivityFormData => {
    const newType = activityTypes.find(t => t.id === newActivityTypeId);
    const keepsPrice = newType?.operationType === 'STOCK_IN' || newType?.operationType === 'STOCK_OUT';

    return {
      feId: prev.feId,
      bid: prev.bid,
      amount: prev.amount,
      activityTypeId: newActivityTypeId,
      productId: prev.productId,
      selectedProduct: prev.selectedProduct,
      clientId: prev.clientId,
      selectedClient: prev.selectedClient,
      unitPrice: keepsPrice ? prev.unitPrice : undefined,
    };
  };

  const selectedType = activityTypes.find(t => t.id === activity.activityTypeId);
  const showPricing = selectedType?.operationType === 'STOCK_IN' || selectedType?.operationType === 'STOCK_OUT';
  const priceLabel = selectedType?.operationType === 'STOCK_IN' ? 'Ціна закупки (USD)' : 'Ціна продажу (USD)';
  const totalPrice = activity.unitPrice !== undefined && activity.amount
    ? activity.unitPrice * activity.amount
    : undefined;

  const isFieldRequired = (activityTypeId: number | undefined, fieldName: 'client' | 'provider' | 'stock' | 'stockFrom'): boolean => {
    if (!activityTypeId || !operationTypesLoaded) return false;
    
    const selectedType = activityTypes.find(t => t.id === activityTypeId);
    if (!selectedType) return false;

    // Check client and provider from activityType.requiredFields
    if (fieldName === 'client') {
      return selectedType.requiredFields.clientRequired;
    }
    if (fieldName === 'provider') {
      return selectedType.requiredFields.providerRequired;
    }

    // Check stock from activityType.requiredFields
    if (fieldName === 'stock') {
      return selectedType.requiredFields.stockRequired;
    }

    if (fieldName === 'stockFrom') {
      return selectedType.operationType === 'BETWEEN_STOCKS';
    }

    return false;
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardHeader
        title={`Активність ${index + 1}`}
        action={
          <>
            <IconButton
              title="Копіювати активність"
              onClick={onCopy}
            >
              <ContentCopyIcon />
            </IconButton>
            {showRemoveButton && (
              <IconButton
                color="error"
                onClick={onRemove}
              >
                <DeleteIcon />
              </IconButton>
            )}
          </>
        }
      />
      <CardContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            <TextField
              select
              label="Тип активності"
              value={activity.activityTypeId ?? ''}
              onChange={(e) =>
                onUpdate(prev => setActivityTypeAndClear(prev, parseInt(e.target.value)))
              }
              required
              disabled={activityTypes.length === 0}
              helperText={activityTypes.length === 0 ? 'Завантаження типів...' : ''}
            >
              {activityTypes.map((type) => (
                <MenuItem key={type.id} value={type.id}>
                  {type.name}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              type="number"
              label="Кількість"
              value={activity.amount}
              onChange={(e) =>
                onUpdate(prev => ({ ...prev, amount: parseInt(e.target.value) }))
              }
              slotProps={{ htmlInput: { min: 1 } }}
              required
              error={!activity.amount || activity.amount <= 0}
              helperText={
                activity.productId && (stockBalance !== undefined || stockFromBalance !== undefined)
                  ? (
                    <Typography
                      component="span"
                      variant="caption"
                      sx={{ cursor: 'pointer', textDecoration: 'underline', color: 'text.secondary' }}
                      onClick={() => onNavigateToAvailability?.(activity.productId!)}
                    >
                      {[
                        stockBalance !== undefined ? `Залишок: ${stockBalance}${stockAverageUnitCost != null ? ` ($${stockAverageUnitCost.toFixed(2)} / шт)` : ''}` : null,
                        stockFromBalance !== undefined ? `Залишок (звідки): ${stockFromBalance}${stockFromAverageUnitCost != null ? ` ($${stockFromAverageUnitCost.toFixed(2)} / шт)` : ''}` : null,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </Typography>
                  )
                  : undefined
              }
            />
          </Box>

          {showPricing && (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <TextField
                type="number"
                label={priceLabel}
                value={activity.unitPrice ?? ''}
                onChange={(e) =>
                  onUpdate(prev => ({
                    ...prev,
                    unitPrice: e.target.value === '' ? undefined : parseFloat(e.target.value),
                  }))
                }
                slotProps={{ htmlInput: { min: 0, step: 0.01 } }}
                required
                  error={activity.unitPrice === undefined || activity.unitPrice <= 0}
              />

              <TextField
                label="Всього (USD)"
                value={totalPrice !== undefined ? `$${totalPrice.toFixed(2)}` : '—'}
                slotProps={{ htmlInput: { readOnly: true } }}
                disabled
              />
            </Box>
          )}

          {activity.margin != null && (
            <Typography variant="caption" color="text.secondary">
              Маржа: ${activity.margin.toFixed(2)}
              {activity.unitCostBasis != null ? ` (собівартість $${activity.unitCostBasis.toFixed(2)}/шт)` : ''}
            </Typography>
          )}

          <Box data-field-target={`${activity.feId}:product`}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Продукт *
            </Typography>
            {activity.selectedProduct ? (
              <Chip
                avatar={activity.selectedProduct.mainImageId ? (
                  <Avatar src={`${API_BASE_URL}/images/${activity.selectedProduct.mainImageId}`} />
                ) : undefined}
                icon={!activity.selectedProduct.mainImageId ? <InventoryIcon /> : undefined}
                label={activity.selectedProduct.name}
                onDelete={onRemoveProduct}
                color="primary"
                variant="outlined"
                sx={{
                  width: '100%',
                  justifyContent: 'space-between',
                  height: SELECTED_CHIP_HEIGHT,
                  '& .MuiChip-avatar': { width: 40, height: 40 },
                }}
              />
            ) : (
              <Button
                variant="outlined"
                startIcon={<InventoryIcon />}
                onClick={onOpenProductSelector}
                fullWidth
                  sx={{ justifyContent: 'flex-start' }}
                color={!activity.selectedProduct ? 'error' : 'primary'}
              >
                Вибрати продукт
              </Button>
            )}
          </Box>

          {isFieldRequired(activity.activityTypeId, 'stockFrom') && (
            <Box data-field-target={`${activity.feId}:stockFrom`}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Зі складу *
              </Typography>
              {activity.selectedStockFrom ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Chip
                    icon={<WarehouseIcon />}
                    label={activity.selectedStockFrom.name}
                    onDelete={onRemoveStockFrom}
                    color="primary"
                    variant="outlined"
                    sx={{ flex: 1, justifyContent: 'space-between', height: SELECTED_CHIP_HEIGHT }}
                  />
                </Box>
              ) : (
                <Button
                  variant="outlined"
                  startIcon={<WarehouseIcon />}
                  onClick={onOpenStockFromSelector}
                  fullWidth
                      sx={{ justifyContent: 'flex-start' }}
                  color="error"
                >
                  Вибрати склад (Звідки)
                </Button>
              )}
            </Box>
          )}

          {isFieldRequired(activity.activityTypeId, 'stock') && (
            <Box data-field-target={`${activity.feId}:stock`}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Склад *
              </Typography>
              {activity.selectedStock ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Chip
                    icon={<WarehouseIcon />}
                    label={activity.selectedStock.name}
                    onDelete={onRemoveStock}
                    color="primary"
                    variant="outlined"
                    sx={{ flex: 1, justifyContent: 'space-between', height: SELECTED_CHIP_HEIGHT }}
                  />
                </Box>
              ) : (
                <Button
                  variant="outlined"
                  startIcon={<WarehouseIcon />}
                  onClick={onOpenStockSelector}
                  fullWidth
                      sx={{ justifyContent: 'flex-start' }}
                  color="error"
                >
                  Вибрати склад
                </Button>
              )}
            </Box>
          )}

          {(isFieldRequired(activity.activityTypeId, 'client') || activity.selectedClient) && (
            <Box data-field-target={`${activity.feId}:client`}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Клієнт {isFieldRequired(activity.activityTypeId, 'client') ? '*' : ''}
              </Typography>
              {activity.selectedClient ? (
                <>
                  <Chip
                    icon={<PersonIcon />}
                    label={formatContactFullName(activity.selectedClient)}
                    onDelete={onRemoveClient}
                    color="primary"
                    variant="outlined"
                    sx={{ width: '100%', justifyContent: 'space-between', height: SELECTED_CHIP_HEIGHT }}
                  />
                  {activity.clientOrganizationName && (
                    <Typography variant="caption" color="text.secondary">
                      Організація: {activity.clientOrganizationName}
                    </Typography>
                  )}
                </>
              ) : (
                <Button
                  variant="outlined"
                  startIcon={<PersonIcon />}
                  onClick={onOpenClientSelector}
                  fullWidth
                  sx={{ justifyContent: 'flex-start' }}
                  color="error"
                >
                  Вибрати клієнта
                </Button>
              )}
            </Box>
          )}

          {(isFieldRequired(activity.activityTypeId, 'provider') || activity.selectedProvider) && (
            <Box data-field-target={`${activity.feId}:provider`}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Постачальник {isFieldRequired(activity.activityTypeId, 'provider') ? '*' : ''}
              </Typography>
              {activity.selectedProvider ? (
                <>
                  <Chip
                    icon={<LocalShippingIcon />}
                    label={formatContactFullName(activity.selectedProvider)}
                    onDelete={onRemoveProvider}
                    color="primary"
                    variant="outlined"
                    sx={{ width: '100%', justifyContent: 'space-between', height: SELECTED_CHIP_HEIGHT }}
                  />
                  {activity.providerOrganizationName && (
                    <Typography variant="caption" color="text.secondary">
                      Організація: {activity.providerOrganizationName}
                    </Typography>
                  )}
                </>
              ) : (
                <Button
                  variant="outlined"
                  startIcon={<LocalShippingIcon />}
                  onClick={onOpenProviderSelector}
                  fullWidth
                  sx={{ justifyContent: 'flex-start' }}
                  color="error"
                >
                  Вибрати постачальника
                </Button>
              )}
            </Box>
          )}
        </Box>

        {serverErrors && serverErrors.length > 0 && (
          <Box sx={{ mt: 2 }}>
            {serverErrors.map((msg, i) => (
              <Alert key={i} severity="error" sx={{ mb: 1 }}>
                {msg}
              </Alert>
            ))}
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
